import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import {
  faAppleWhole, faArrowsRotate, faAtom, faBaseball, faBasketball, faBicycle, faBolt,
  faBook, faBookOpen, faBrain, faBug, faBurger, faCalculator, faCalendar, faCamera,
  faCar, faCarrot, faCartShopping, faChartColumn, faChartLine, faChartPie, faChess,
  faChessKnight, faClock, faCloud, faCode, faCodeBranch, faCoins, faComments,
  faCompass, faCookie, faCubes, faDatabase, faDiagramProject, faDice, faDna,
  faDragon, faEarthAmericas, faEnvelope, faEye, faFilm, faFingerprint, faFire,
  faFish, faFlask, faFootball, faFutbol, faGamepad, faGears, faGhost, faGlobe,
  faGraduationCap, faHatWizard, faHeart, faHeartPulse, faIceCream, faImage, faKey,
  faKeyboard, faLaptopCode, faLeaf, faLightbulb, faListOl, faLock,
  faMagnifyingGlass, faMap, faMedal, faMicroscope, faMoneyBill, faMoon, faMusic,
  faPaintbrush, faPalette, faPaw, faPen, faPersonRunning, faPiggyBank,
  faPizzaSlice, faPlane, faPuzzlePiece, faRepeat, faRobot, faRocket, faSeedling,
  faShapes, faShip, faShuffle, faSitemap, faSnowflake, faStar, faStopwatch,
  faStore, faSun, faTable, faTemperatureHalf, faTerminal, faThumbsUp,
  faTrafficLight, faTree, faTrophy, faUmbrella, faUserAstronaut, faVial,
  faVolleyball, faWandMagicSparkles,
} from "@fortawesome/free-solid-svg-icons";

/**
 * Lesson icon vocabulary. Keep in sync with LESSON_ICON_NAMES in
 * services/api/src/bedrock/lesson-ai-prompts.ts (the AI picks from that list).
 * Unknown names fall back to a gradient tile, so drift degrades gracefully.
 */
export const LESSON_ICON_MAP: Record<string, IconDefinition> = {
  "calculator": faCalculator, "robot": faRobot, "gamepad": faGamepad, "dice": faDice,
  "rocket": faRocket, "flask": faFlask, "code": faCode, "terminal": faTerminal,
  "keyboard": faKeyboard, "laptop-code": faLaptopCode, "bug": faBug, "gears": faGears,
  "wand-magic-sparkles": faWandMagicSparkles, "puzzle-piece": faPuzzlePiece,
  "shapes": faShapes, "cubes": faCubes, "list-ol": faListOl,
  "arrows-rotate": faArrowsRotate, "repeat": faRepeat, "shuffle": faShuffle,
  "code-branch": faCodeBranch, "sitemap": faSitemap, "diagram-project": faDiagramProject,
  "database": faDatabase, "table": faTable, "chart-line": faChartLine,
  "chart-pie": faChartPie, "chart-column": faChartColumn,
  "magnifying-glass": faMagnifyingGlass, "lightbulb": faLightbulb, "brain": faBrain,
  "graduation-cap": faGraduationCap, "book": faBook, "book-open": faBookOpen,
  "pen": faPen, "comments": faComments, "envelope": faEnvelope, "music": faMusic,
  "palette": faPalette, "paintbrush": faPaintbrush, "image": faImage,
  "camera": faCamera, "film": faFilm, "star": faStar, "heart": faHeart,
  "bolt": faBolt, "fire": faFire, "snowflake": faSnowflake, "sun": faSun,
  "moon": faMoon, "cloud": faCloud, "umbrella": faUmbrella, "tree": faTree,
  "leaf": faLeaf, "seedling": faSeedling, "paw": faPaw, "fish": faFish,
  "dragon": faDragon, "ghost": faGhost, "hat-wizard": faHatWizard,
  "trophy": faTrophy, "medal": faMedal, "stopwatch": faStopwatch, "clock": faClock,
  "calendar": faCalendar, "map": faMap, "compass": faCompass, "globe": faGlobe,
  "earth-americas": faEarthAmericas, "car": faCar, "plane": faPlane, "ship": faShip,
  "bicycle": faBicycle, "traffic-light": faTrafficLight, "money-bill": faMoneyBill,
  "coins": faCoins, "piggy-bank": faPiggyBank, "cart-shopping": faCartShopping,
  "store": faStore, "pizza-slice": faPizzaSlice, "burger": faBurger,
  "ice-cream": faIceCream, "cookie": faCookie, "apple-whole": faAppleWhole,
  "carrot": faCarrot, "dna": faDna, "atom": faAtom, "microscope": faMicroscope,
  "vial": faVial, "temperature-half": faTemperatureHalf, "heart-pulse": faHeartPulse,
  "user-astronaut": faUserAstronaut, "person-running": faPersonRunning,
  "futbol": faFutbol, "basketball": faBasketball, "baseball": faBaseball,
  "football": faFootball, "volleyball": faVolleyball, "chess": faChess,
  "chess-knight": faChessKnight, "lock": faLock, "key": faKey,
  "fingerprint": faFingerprint, "eye": faEye, "thumbs-up": faThumbsUp,
};

export const LESSON_ICON_NAMES: readonly string[] = Object.keys(LESSON_ICON_MAP).sort((a, b) =>
  a.localeCompare(b),
);

/** Human-readable label for an icon slug (e.g. "wand-magic-sparkles" → "wand magic sparkles"). */
export function formatLessonIconLabel(name: string): string {
  return name.replace(/-/g, " ");
}

export function lessonIconDefinition(name: string | null | undefined): IconDefinition | null {
  if (!name) return null;
  return LESSON_ICON_MAP[name.trim().toLowerCase()] ?? null;
}

const FALLBACK_GRADIENTS: readonly [string, string][] = [
  ["#1b4d2e", "#6ba336"],
  ["#2a4d8f", "#5aa9e6"],
  ["#7a3b8f", "#c86bd1"],
  ["#8f4d2a", "#e69a5a"],
  ["#2a8f7a", "#6bd1b8"],
  ["#8f2a4d", "#e65a8a"],
  ["#4d6b1b", "#a3c836"],
  ["#36558f", "#8a9ee6"],
];

function hashString(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = (h * 31 + s.charCodeAt(i)) | 0;
  }
  return Math.abs(h);
}

/** Deterministic gradient for lessons without a chosen icon/background. */
export function fallbackGradient(seed: string): string {
  const [from, to] = FALLBACK_GRADIENTS[hashString(seed) % FALLBACK_GRADIENTS.length]!;
  return `linear-gradient(135deg, ${from} 0%, ${to} 100%)`;
}
